import { useMemo, useRef, useState } from 'react'
import { Braces, Check, ChevronsUpDown } from 'lucide-react'

import type { WorkflowVariableSchema } from '@/gen/cortado/v1/cortado_pb'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  formatVariableLabel,
  getTemplateVariableParentType,
  templateVariableGroupIndex,
} from '@/lib/template-variable-groups'
import { toTemplateVariableSyntax } from '@/lib/template-variable-tokens'

/**
 * How a picked variable is serialized into the field:
 * - "template": Go template syntax, `{{ .entity.contact.email }}` — for email
 *   subject/body fields rendered by the backend template engine.
 * - "path": bare dot path, `entity.contact.email` — for to_field-style
 *   recipient lookups.
 */
export type VariableSyntax = 'template' | 'path'

/** A selectable recipient path with a human label (for VariablePathSelect). */
export type VariablePathOption = {
  path: string
  label: string
  example?: string
}

export function formatVariable(path: string, syntax: VariableSyntax): string {
  return syntax === 'template' ? toTemplateVariableSyntax(path) : path
}

/**
 * Best-effort human label for a path that has no server-provided label.
 * `entity.contact.email` -> "Contact email"; `org.name` -> "Organization
 * name". Delegates to the same label/grouping taxonomy every
 * template-variable picker in the app shares — see
 * `@/lib/template-variable-groups`.
 */
export function humanizeVariablePath(path: string): string {
  return formatVariableLabel(path)
}

/**
 * Bucket path-bearing items into ordered, labelled groups for display. Groups
 * the order list doesn't know about are appended rather than filtered out —
 * dropping them would make every variable in a newly-classified group vanish
 * from this picker with no error.
 */
export function groupByCategory<T extends { path: string }>(
  items: T[]
): Array<{ group: string; items: T[] }> {
  const buckets = new Map<string, T[]>()
  for (const item of items) {
    const group = getTemplateVariableParentType(item.path)
    const existing = buckets.get(group)
    if (existing) existing.push(item)
    else buckets.set(group, [item])
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => templateVariableGroupIndex(a) - templateVariableGroupIndex(b))
    .map(([group, groupItems]) => ({ group, items: groupItems }))
}

/** Secondary line for a picker item: an example value, falling back to the path. */
function variableHint(value: { example?: string; path: string }): string {
  return value.example ? `e.g. ${value.example}` : value.path
}

function insertAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  current: string,
  insert: string
): { next: string; caret: number } {
  if (!el) {
    return { next: current + insert, caret: (current + insert).length }
  }
  const start = el.selectionStart ?? current.length
  const end = el.selectionEnd ?? start
  const next = current.slice(0, start) + insert + current.slice(end)
  return { next, caret: start + insert.length }
}

function VariableMenu({
  variables,
  onPick,
  align = 'end',
}: {
  variables: WorkflowVariableSchema[]
  onPick: (variable: WorkflowVariableSchema) => void
  align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const groups = useMemo(() => groupByCategory(variables), [variables])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-1 px-2 text-xs"
          aria-label="Insert variable"
          disabled={variables.length === 0}
        >
          <Braces className="size-3.5" />
          Variable
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align={align}>
        <Command>
          <CommandInput placeholder="Search variables..." />
          <CommandList>
            <CommandEmpty>No variable found.</CommandEmpty>
            {groups.map(({ group, items }) => (
              <CommandGroup key={group} heading={group}>
                {items.map((variable) => (
                  <CommandItem
                    key={variable.path}
                    value={`${variable.label} ${variable.path}`}
                    onSelect={() => {
                      onPick(variable)
                      setOpen(false)
                    }}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {variable.label}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {variableHint(variable)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        <p className="border-t px-3 py-2 text-[11px] leading-snug text-muted-foreground">
          Inserts a placeholder that's filled with real data when the email
          sends.
        </p>
      </PopoverContent>
    </Popover>
  )
}

/** Single-line input with an "Insert variable" affordance. */
export function VariableInput({
  value,
  onChange,
  variables,
  syntax,
  placeholder,
  id,
  className,
  'aria-invalid': ariaInvalid,
}: {
  value: string
  onChange: (value: string) => void
  variables: WorkflowVariableSchema[]
  syntax: VariableSyntax
  placeholder?: string
  id?: string
  className?: string
  'aria-invalid'?: boolean
}) {
  const ref = useRef<HTMLInputElement>(null)

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Input
        ref={ref}
        id={id}
        value={value}
        placeholder={placeholder}
        aria-invalid={ariaInvalid}
        onChange={(event) => onChange(event.target.value)}
      />
      <VariableMenu
        variables={variables}
        onPick={(variable) => {
          const { next, caret } = insertAtCursor(
            ref.current,
            value,
            formatVariable(variable.path, syntax)
          )
          onChange(next)
          requestAnimationFrame(() => {
            ref.current?.focus()
            ref.current?.setSelectionRange(caret, caret)
          })
        }}
      />
    </div>
  )
}

/**
 * Recipient field picker for to_field-style fields. Presents a constrained list
 * of valid recipient paths by their human label (e.g. "Contact email") rather
 * than the raw dot-path, grouped by category, with a check on the active one.
 */
export function VariablePathSelect({
  value,
  onChange,
  options,
  placeholder = 'Select a recipient field',
  id,
  'aria-invalid': ariaInvalid,
}: {
  value: string
  onChange: (value: string) => void
  options: VariablePathOption[]
  placeholder?: string
  id?: string
  'aria-invalid'?: boolean
}) {
  const [open, setOpen] = useState(false)
  const groups = useMemo(() => groupByCategory(options), [options])
  const selected = options.find((option) => option.path === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {selected ? selected.label : value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search fields..." />
          <CommandList>
            <CommandEmpty>No field found.</CommandEmpty>
            {groups.map(({ group, items }) => (
              <CommandGroup key={group} heading={group}>
                {items.map((option) => (
                  <CommandItem
                    key={option.path}
                    value={`${option.label} ${option.path}`}
                    onSelect={() => {
                      onChange(option.path)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4 shrink-0',
                        value === option.path ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm">{option.label}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {variableHint(option)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
